import { useEffect, useState, type ReactElement } from 'react';
import { getPlatformHealth, type PlatformHealthSnapshot } from '../api.js';
import { useChrome } from '../chrome.js';
import { BackLink } from '../components/BackLink.js';
import { useSession } from '../useSession.js';

type Load =
  | { status: 'loading' }
  | { status: 'ready'; snapshot: PlatformHealthSnapshot }
  | { status: 'forbidden' }
  | { status: 'error' };

/**
 * How each database state reads to an operator.
 *
 * ⚠️ A MAP RATHER THAN A LITERAL, deliberately. This cell used to render the string 'available'
 * unconditionally, which meant the panel asserted a green it had never read — the exact failure this
 * page exists to avoid, sitting in the page itself. Indexing the field makes the rendered word a
 * function of the payload, and widening the type later fails the build here until this map learns the
 * new state rather than quietly showing the old one.
 */
const DATABASE_STATE: Record<PlatformHealthSnapshot['database'], string> = {
  ok: 'available',
};

/**
 * What the counts cover, in the operator's words.
 *
 * ⚠️ READ FROM THE PAYLOAD FOR THE SAME REASON `DATABASE_STATE` IS. The first draft of the scope
 * label typed "this tenant" straight into the markup — the identical defect this file had already
 * fixed one field earlier, and `orphan:fields` caught it for the identical reason: a page asserting
 * something it never read. If the server ever reports a wider scope, the sentence must change with it
 * rather than keep insisting on the old one.
 */
const SCOPE_LABEL: Record<PlatformHealthSnapshot['scope'], string> = {
  tenant: 'this tenant',
};

/** A count of failed runs, where zero reads as the good news it is rather than as a bare "0". */
function failedRuns(n: number): string {
  return n === 0 ? 'none' : `${n} failed run${n === 1 ? '' : 's'}`;
}

/** Seam failures with their timeouts broken out — a timeout is the seam's most common way to fail. */
function seamText(seam: NonNullable<PlatformHealthSnapshot['seam']>): string {
  if (seam.failedRuns === 0) return 'none';
  return `${failedRuns(seam.failedRuns)} (${seam.timeouts} timeout${seam.timeouts === 1 ? '' : 's'})`;
}

/**
 * The alarm reading, naming its SCOPE — every other number on this page is this tenant's, and an alarm
 * belongs to the deployment. "none firing" is only ever printed from a real reading: an unread alarm
 * state is `null` and renders as `unknown` (see the row), never as the all-clear.
 */
function alarmsText(alarms: NonNullable<PlatformHealthSnapshot['alarms']>): string {
  const where = `${alarms.scope}-wide`;
  return alarms.firing.length === 0
    ? `none firing (${where})`
    : `${alarms.firing.join(', ')} (${where})`;
}

/**
 * A failure count, but only when there was traffic for it to describe.
 *
 * 🔴 "none" OVER ZERO RUNS IS A FALSE ALL-CLEAR. If the tenant started nothing in the window — or Nexus
 * is down so hard that runs are never created — every failure count is 0, and "none" reads as healthy
 * with no evidence either way (red-team, row G). So with no runs, the cell says so instead. And an absent
 * field (an older api mid-deploy) is `unknown`, never a guess.
 */
function failureCell(
  value: number | undefined,
  totalRuns: number,
  render: (n: number) => string,
): string {
  if (value === undefined) return 'unknown';
  if (totalRuns === 0) return 'no runs in window';
  return render(value);
}

export function PlatformHealthPanel({ snapshot }: { snapshot: PlatformHealthSnapshot }) {
  const unavailable = new Set(snapshot.unavailable);
  const { queued, running, awaitingIndex, succeeded, failed, canceled } = snapshot.runs;
  const totalRuns = queued + running + awaitingIndex + succeeded + failed + canceled;
  const runRows = [
    ['Queued', snapshot.runs.queued],
    ['Running', snapshot.runs.running],
    ['Awaiting index', snapshot.runs.awaitingIndex],
    ['Succeeded', snapshot.runs.succeeded],
    ['Failed', snapshot.runs.failed],
    ['Canceled', snapshot.runs.canceled],
    ['Unclassified failures', snapshot.runs.unclassifiedFailures],
  ] as const;

  return (
    <>
      <section className="card">
        <h2 className="card__title">Platform health</h2>
        <p className="card__note">
          Runs in <strong>{SCOPE_LABEL[snapshot.scope]}</strong> over the last{' '}
          {snapshot.windowHours} hours. Checked at{' '}
          <time dateTime={snapshot.checkedAt}>{new Date(snapshot.checkedAt).toLocaleString()}</time>
          .
          {/* The age of the snapshot is part of the snapshot: a health claim with no time on it
              cannot be distinguished from a stale one, and this page is read during an incident. */}
        </p>
        <table className="table">
          <thead>
            <tr>
              <th>Signal</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Database</td>
              <td>{DATABASE_STATE[snapshot.database]}</td>
            </tr>
            <tr>
              {/* Runs the seam BROKE, from the run records. Not latency — nothing measures that yet, so
                  the label no longer claims it. */}
              <td>Seam (Nexus) — runs it failed</td>
              <td>
                {failureCell(snapshot.seam?.failedRuns, totalRuns, () =>
                  snapshot.seam ? seamText(snapshot.seam) : 'unknown',
                )}
              </td>
            </tr>
            <tr>
              {/* Runs that died at the `extract` stage: documents with no text AND, since #1196, an
                  extractor that threw (the workers stage it at the boundary). Runs that failed before
                  that deploy were staged `analyze` and age out of the window. */}
              <td>Extraction — runs it failed</td>
              <td>{failureCell(snapshot.extraction?.failedRuns, totalRuns, failedRuns)}</td>
            </tr>
            <tr>
              <td>Alarm state</td>
              <td>
                {snapshot.alarms === null || unavailable.has('alarms')
                  ? 'unknown'
                  : alarmsText(snapshot.alarms)}
              </td>
            </tr>
          </tbody>
        </table>
      </section>
      <section className="card">
        <h3 className="card__title">Recent runs in {SCOPE_LABEL[snapshot.scope]}</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {runRows.map(([label, value]) => (
              <tr key={label}>
                <td>{label}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}

export function PlatformHealth() {
  const session = useSession();
  const Chrome = useChrome();
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    if (session.status !== 'authed') return;
    void getPlatformHealth().then((result) => {
      if (result.kind === 'ok') setLoad({ status: 'ready', snapshot: result.snapshot });
      else setLoad({ status: result.kind });
    });
  }, [session.status]);

  if (session.status !== 'authed' || load.status === 'loading') {
    return <div className="app__loading">Loading...</div>;
  }

  const shell = (body: ReactElement) => (
    <div className="app">
      {Chrome !== null && <Chrome claims={session.claims} />}
      <main className="app__main">
        <div className="app__crumbs">
          <BackLink fallback="/" label="Back" />
        </div>
        <div className="app__intro">
          <h1>Platform health</h1>
          <p className="sub">
            What the application can prove about its current operating state — for this tenant.
          </p>
        </div>
        {body}
      </main>
    </div>
  );

  if (load.status === 'forbidden') {
    // The capability is missing, not the session (ADR-0064 D3). Says what is true and what the fix is,
    // rather than implying a role change would grant it — it would not; the grant is seeded by hand.
    return shell(<div className="notice">This account does not have platform-health access.</div>);
  }
  if (load.status === 'error') {
    return shell(
      <div className="notice">Platform health is unavailable. Nothing was changed.</div>,
    );
  }
  return shell(<PlatformHealthPanel snapshot={load.snapshot} />);
}
